import moment from "moment";
import Vue from "vue";
import apiService from "./api";

// Mesi precaricati in background a partire da oggi
const MESI_ORIZZONTE = 12;
const MESI_BATCH = 3;

// Durata massima del totale selezionato
const MAX_NOTTI = 30;

/**
 * version forza la reattività di Vue
 */
const state = Vue.observable({ version: 0 });

let attivo = false;
let giorni = {};
let mesiRichiesti = {};
let soggiorni = {};
let soggiorniRichiesti = {};
let struttura = { db: "", idhotel: "", chiave: "" };
let coda = [];
let codaAttiva = false;

/** Lettura volutamente a vuoto: è così che le celle dichiarano a Vue di dipendere dai blocchi. */
function tracciaDipendenza() {
  return state.version;
}

/** Legge un marcatore del giorno: senza il parametro della struttura non ce n'è nessuno. */
function marcatore(data, campo) {
  tracciaDipendenza();
  const giorno = attivo ? giorni[data] : null;
  return !!giorno && !!giorno[campo];
}

/** Toglie i centesimi a zero: in una cella o in un tooltip non aggiungono nulla. */
function senzaCentesimi(prezzo) {
  return prezzo.replace(",00", "");
}

/** Marca i mesi del blocco come richiesti e ne restituisce gli estremi. */
function segnaBlocco(primoGiorno, numMesi) {
  const inizioMese = primoGiorno.clone().startOf("month");
  for (let i = 0; i < numMesi; i++) {
    mesiRichiesti[inizioMese.clone().add(i, "months").format("YYYY-MM")] = true;
  }
  return {
    dadata: primoGiorno.format("YYYY-MM-DD"),
    adata: inizioMese.clone().add(numMesi, "months").format("YYYY-MM-DD")
  };
}

/** Scarica il primo blocco in coda, poi richiama sé stesso quando il browser è libero. */
function prossimoBlocco() {
  const blocco = coda.shift();
  if (!blocco) {
    codaAttiva = false;
    return;
  }
  const chiave = struttura.chiave;
  apiService
    .instant_get("BE_calendario/" + struttura.db + "/" + struttura.idhotel + "/" + blocco.dadata + "/" + blocco.adata)
    .then((resp) => {
      // struttura cambiata mentre la richiesta era in volo: il risultato non vale più
      if (chiave === struttura.chiave && resp && resp.giorni) {
        Object.assign(giorni, resp.giorni);
        // una sola notifica per blocco
        state.version++;
      }
    })
    .catch(() => {
      // senza risposta le celle restano quelle standard: la selezione delle date non cambia
    })
    .then(() => {
      if (typeof window.requestIdleCallback === "function") {
        window.requestIdleCallback(prossimoBlocco, { timeout: 2000 });
      } else {
        setTimeout(prossimoBlocco, 200);
      }
    });
}

/**
 * Recupera i totali di tutte le durate per un arrivo
 */
function caricaSoggiorni(dadata) {
  if (!struttura.chiave || soggiorniRichiesti[dadata]) {
    return;
  }
  soggiorniRichiesti[dadata] = true;
  const chiave = struttura.chiave;
  apiService
    .instant_get("BE_calendario/soggiorni/" + struttura.db + "/" + struttura.idhotel + "/" + dadata + "/" + MAX_NOTTI)
    .then((resp) => {
      // struttura cambiata mentre la richiesta era in volo: il risultato non vale più
      if (chiave === struttura.chiave && resp && resp.totali) {
        soggiorni[dadata] = resp.totali;
        state.version++;
      }
    })
    .catch(() => {
      // senza risposta il tooltip resta con il solo conteggio delle notti
    });
}

/** Avvia il consumo della coda, se non è già in corso. */
function avviaCoda() {
  if (coda.length && !codaAttiva) {
    codaAttiva = true;
    prossimoBlocco();
  }
}

const availabilityService = {
  /**
   * Abilita la disponibilità in calendario, come da parametri generali BE della struttura
   *
   * @param {boolean} valore
   */
  setEnabled(valore) {
    if (attivo === !!valore) {
      return;
    }
    attivo = !!valore;
    state.version++;
  },

  /**
   * Precarica in background la disponibilità dei prossimi mesi
   *
   * @param {string} db
   * @param {string} idhotel
   */
  preload(db, idhotel) {
    if (!attivo || !db || !idhotel) {
      return;
    }
    if (db + "|" + idhotel !== struttura.chiave) {
      // altra struttura: quanto c'era in cache non vale più
      struttura = { db: db, idhotel: idhotel, chiave: db + "|" + idhotel };
      giorni = {};
      mesiRichiesti = {};
      soggiorni = {};
      soggiorniRichiesti = {};
      coda = [];
      state.version++;
    }

    const oggi = moment().startOf("day");
    for (let i = 0; i < MESI_ORIZZONTE; i += MESI_BATCH) {
      // il primo blocco parte da oggi, gli altri dall'inizio del loro mese
      const primoGiorno = i === 0 ? oggi.clone() : oggi.clone().add(i, "months").startOf("month");
      if (!mesiRichiesti[primoGiorno.format("YYYY-MM")]) {
        coda.push(segnaBlocco(primoGiorno, Math.min(MESI_BATCH, MESI_ORIZZONTE - i)));
      }
    }
    avviaCoda();
  },

  /**
   * Carica il mese indicato se non è in cache: serve oltre l'orizzonte precaricato.
   * @param {string} db
   * @param {string} idhotel
   * @param {Date|string} data una data qualsiasi del mese
   */
  ensureMonth(db, idhotel, data) {
    if (db + "|" + idhotel !== struttura.chiave) {
      return;
    }
    const mese = moment(data).startOf("month");
    if (!mese.isValid() || mesiRichiesti[mese.format("YYYY-MM")]) {
      return;
    }
    coda.push(segnaBlocco(mese, 1));
    avviaCoda();
  },

  /**
   * Prezzo minimo della notte
   *
   * @param {string} data nel formato YYYY-MM-DD
   * @returns {string} stringa vuota se il giorno non è (ancora) noto
   */
  price(data) {
    tracciaDipendenza();
    const giorno = attivo ? giorni[data] : null;
    return giorno && giorno.p ? senzaCentesimi(giorno.p) : "";
  },

  /**
   * Prezzo minimo di un soggiorno, calcolato dal backend con le regole di BE_camere
   *
   * @param {string} dadata arrivo, nel formato YYYY-MM-DD
   * @param {string} adata partenza, nel formato YYYY-MM-DD
   * @returns {string} stringa vuota se il soggiorno non è prenotabile o non è ancora noto
   */
  stayPrice(dadata, adata) {
    tracciaDipendenza();
    if (!attivo) {
      return "";
    }
    const arrivo = moment(dadata, "YYYY-MM-DD");
    const partenza = moment(adata, "YYYY-MM-DD");
    if (!arrivo.isValid() || !partenza.isValid()) {
      return "";
    }
    const notti = partenza.diff(arrivo, "days");
    if (notti < 1 || notti > MAX_NOTTI) {
      return "";
    }
    const totali = soggiorni[dadata];
    if (!totali) {
      caricaSoggiorni(dadata);
      return "";
    }
    return totali[notti] ? senzaCentesimi(totali[notti]) : "";
  },

  /**
   * @param {string} data nel formato YYYY-MM-DD
   * @returns {boolean} true se in quella notte nessuna tariffa è vendibile
   */
  soldOut(data) {
    return marcatore(data, "e");
  },

  /**
   * Minimo di notti richiesto per arrivare in quel giorno
   *
   * @param {string} data nel formato YYYY-MM-DD
   * @returns {number} 0 se il giorno non è noto o non accetta arrivi
   */
  minStay(data) {
    tracciaDipendenza();
    const giorno = attivo ? giorni[data] : null;
    return giorno && giorno.m ? giorno.m : 0;
  },

  /**
   * @param {string} data nel formato YYYY-MM-DD
   * @returns {boolean} true se nessuna tipologia accetta l'arrivo in quel giorno
   */
  closedToArrival(data) {
    return marcatore(data, "a");
  },

  /**
   * @param {string} data nel formato YYYY-MM-DD
   * @returns {boolean} true se nessuna tipologia accetta la partenza in quel giorno
   */
  closedToDeparture(data) {
    return marcatore(data, "d");
  }
};

export default availabilityService;
